import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';
import IntervalConfigModal from '../IntervalConfigModal/IntervalConfigModal';
import { useTranslation } from 'react-i18next';

interface BulkSendSectionProps {
  selectedLeadsCount: number;
  intervalConfig: { minInterval: number; maxInterval: number };
  onSaveIntervals: (min: number, max: number) => void;
  onSendSelected: () => void;
  isSending: boolean;
}

const BulkSendSection: React.FC<BulkSendSectionProps> = ({
  selectedLeadsCount,
  intervalConfig,
  onSaveIntervals,
  onSendSelected,
  isSending
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleConfigureIntervals = () => {
    setIsModalOpen(true);
  };

  const isSendDisabled = selectedLeadsCount === 0 || isSending;

  return (
    <>
      <motion.div
        className="flex flex-col gap-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.5 }}
      >
        <motion.h3
          className="text-lg font-bold text-[#181411] dark:text-white"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1 }}
        >
          {t('sidebar.bulk_send_title')}
        </motion.h3>

        <motion.div
          className="flex flex-col gap-3 rounded-xl p-4 bg-white dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/50 card"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.1, duration: 0.5 }}
        >
          <motion.button
            onClick={onSendSelected}
            disabled={isSendDisabled}
            className="flex h-12 w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gradient-primary text-white gap-2 text-sm font-bold leading-normal tracking-[0.015em] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:active:scale-100"
            whileHover={{ scale: isSendDisabled ? 1 : 1.02 }}
            whileTap={{ scale: isSendDisabled ? 1 : 0.98 }}
          >
            {isSending ? (
              <>
                <span className="material-symbols-outlined animate-spin">sync</span>
                {t('sidebar.bulk_sending')}
              </>
            ) : (
              <>
                <span className="material-symbols-outlined">send</span>
                {t('sidebar.bulk_send_button')} ({selectedLeadsCount})
              </>
            )}
          </motion.button>

          <motion.button
            onClick={handleConfigureIntervals}
            className="flex h-12 w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300 gap-2 text-sm font-medium leading-normal hover:bg-gray-200 dark:hover:bg-gray-700/50 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="material-symbols-outlined">timer</span>
            {t('sidebar.bulk_config_button')} ({intervalConfig.minInterval}-{intervalConfig.maxInterval}s)
          </motion.button>
        </motion.div>

        {/* Disclaimer */}
        <motion.div
          className="flex gap-2 items-start p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.4 }}
        >
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
            {t('disclaimers.whatsapp_blocking_message')}
          </p>
        </motion.div>
      </motion.div>

      <IntervalConfigModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={onSaveIntervals}
        initialMin={intervalConfig.minInterval}
        initialMax={intervalConfig.maxInterval}
      />
    </>
  );
};

export default BulkSendSection;